/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect} from 'react'
import {Navigate, Route, Routes, useNavigate} from 'react-router-dom'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {AuthPage, Logout, useAuth} from '../modules/auth'
import {App} from '../App'
import DynamicRoutes from './DynamicRoutes'
import useSetup from '../api/hooks/useSetup'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
    const {currentUser} = useAuth()
    const {data: setup, isLoading: setupLoading} = useSetup()
    const navigate = useNavigate()

    useEffect(() => {
        if (!currentUser) return
        if (!setup || setupLoading) return

        const requiredFields: string[] = setup.profile_modal_edit ?? []

        if (requiredFields.length > 0) {
            const hasEmpty = requiredFields.some((field) => {
                const value = (currentUser as any)[field]
                return value === null || value === undefined || value === ''
            })

            if (hasEmpty) {
                navigate('/profile', {replace: true})
            }
        }
    }, [currentUser, setup, setupLoading, navigate])

    return (
        <Routes>
            <Route element={<App />}>
                <Route path='error/*' element={<ErrorsPage />} />
                <Route path='logout' element={<Logout />} />
                {currentUser ? (
                    <>
                        <Route path='/*' element={<DynamicRoutes />} />
                        <Route index element={<Navigate to='/dashboard' />} />
                    </>
                ) : (
                    <>
                        <Route path='auth/*' element={<AuthPage />} />
                        <Route path='*' element={<Navigate to='/auth' />} />
                    </>
                )}
            </Route>
        </Routes>
    )
}

export {AppRoutes}
